/**
 * A PICK IS A DEADLINE (Tim, 23 August): "for pick selections, include that as a deadline for the
 * relevant GM with action items. Have that present for commishes to track, as well."
 *
 * Every other deadline in this app is a date a commissioner announced weeks ahead. This one is
 * ninety seconds long, arrives fourteen times an evening, and is the only deadline a GM can miss
 * while looking straight at the screen — so it belongs in the same rail as the others rather than
 * living only inside the draft board.
 *
 * WHAT THIS IS NOT. It is not a second draft board and it does not decide anything: the pick clock
 * is advisory everywhere else in this app (`clocks.ts` says so at the top) and it stays advisory
 * here. Nothing auto-picks, nothing is refused. What this produces is the sentence Home puts at
 * the top of the page on draft night — whose pick it is, how long it has been theirs, and what the
 * person reading is meant to do about it.
 *
 * THREE AUDIENCES, ONE DERIVATION:
 *
 *   the man on the clock   "You are on the clock — 1:42 left" and a button into the board.
 *   the man two picks out  "You are up in 2 picks", which is the warning that gets him back.
 *   the commissioner       the same row for whoever is picking, because chasing him is the job.
 *
 * The elapsed/remaining split is the one subtlety. `draftStrip` shows ELAPSED because the league
 * has never timed anybody out and a countdown implies a consequence that does not exist. Here a
 * countdown is honest when — and only when — the commissioner has set a band for the round: he
 * has said out loud how long a pick gets, so the app can say how much of it is left. With no band
 * there is no countdown, only the elapsed clock, and the difference is visible on screen.
 */

import { pickSeconds } from './clocks.js';
import { countdown, type Countdown } from './clocks.js';
import type { DraftClocks } from '../store/types.js';

export type PickDueState = 'off' | 'onclock' | 'ondeck' | 'waiting' | 'done';

export interface PickDueInput {
  /** The draft's own status. Nothing here means anything unless it is live. */
  status: 'setup' | 'live' | 'complete' | null | undefined;
  /** The pick being made right now, from the board. */
  onClock: { code: string; round: number; owner: string; overall: number } | null;
  /** The reader's next unmade pick, and how many picks away it is. Zero away means it is his. */
  mine: { code: string; away: number } | null;
  /** When the previous pick was made — what the current pick's clock runs from. */
  since: string | null;
  clocks?: DraftClocks | null;
  now?: number;
}

export interface PickDue {
  state: PickDueState;
  /** Who is picking, for a commissioner and for anybody watching. */
  onClock: { code: string; round: number; owner: string; overall: number } | null;
  /** The reader's next pick. */
  mine: { code: string; away: number } | null;
  /** The countdown, only where the commissioner has set a band for this round. */
  clock: Countdown | null;
  /** How long the current pick has been sitting, `M:SS`, always available once one has been made. */
  elapsed: string | null;
  /** True once an announced allowance has run out. Advisory — nothing happens. */
  over: boolean;
  /** What the reader is being asked to do, in one line. Empty when the answer is "nothing". */
  action: string;
}

const OFF: PickDue = {
  state: 'off', onClock: null, mine: null, clock: null, elapsed: null, over: false, action: '',
};

/** `M:SS` since a timestamp, capped at 59:59 — the same shape `draftStrip.elapsed` produces. */
function sinceText(since: string | null, now: number): string | null {
  if (!since) return null;
  const t = Date.parse(since);
  if (Number.isNaN(t)) return null;
  const secs = Math.max(0, Math.floor((now - t) / 1000));
  if (secs >= 3600) return '59:59';
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
}

/**
 * `ondeck` is two picks out or fewer, which is `draftStrip`'s own window and is deliberately the
 * same number: a manager should not be told he is on deck by one part of the app and not by
 * another. If that window ever moves it moves in both places.
 */
export const ON_DECK_WITHIN = 2;

export function pickDue(i: PickDueInput): PickDue {
  const now = i.now ?? Date.now();
  if (i.status !== 'live') return OFF;
  if (!i.onClock) {
    /*
     * Live with nothing on the clock means the board is full. `done` rather than `off`, because
     * "the draft is over" is a different thing to say than "there is no draft", and on the night
     * itself the difference is the whole message.
     */
    return { ...OFF, state: i.status === 'live' ? 'done' : 'off' };
  }

  const seconds = pickSeconds(i.clocks, i.onClock.round);
  const clock = countdown(i.since, seconds, now);
  const elapsed = sinceText(i.since, now);
  const away = i.mine?.away ?? null;

  const state: PickDueState = away === 0 ? 'onclock'
    : away !== null && away <= ON_DECK_WITHIN ? 'ondeck'
      : 'waiting';

  const action = state === 'onclock'
    ? 'You are on the clock — make your pick.'
    : state === 'ondeck'
      ? `You are up in ${away} pick${away === 1 ? '' : 's'}.`
      : '';

  return {
    state,
    onClock: i.onClock,
    mine: i.mine ?? null,
    clock,
    elapsed,
    over: clock?.over ?? false,
    action,
  };
}

/**
 * What a commissioner is tracking on the night: one line, and whether it needs him.
 *
 * `chase` is the only judgement in this file, and it is deliberately conservative — an announced
 * allowance has to have actually run out. Without a band nothing is late, because nothing was
 * promised, and a commissioner nudged about a pick nobody put a limit on would stop reading these.
 */
export interface PickWatch {
  /** "RYAN is on the clock — 3.04" or "The board is full". */
  line: string;
  owner: string | null;
  elapsed: string | null;
  clock: Countdown | null;
  chase: boolean;
}

export function pickWatch(d: PickDue): PickWatch | null {
  if (d.state === 'off') return null;
  if (d.state === 'done' || !d.onClock) {
    return { line: 'Every pick is in.', owner: null, elapsed: null, clock: null, chase: false };
  }
  return {
    line: `${d.onClock.owner} is on the clock — ${d.onClock.code}`,
    owner: d.onClock.owner,
    elapsed: d.elapsed,
    clock: d.clock,
    chase: d.over,
  };
}
